import { useQuery } from '@apollo/client';
import { useAuth } from '@contexts/AuthContext';
import { GET_ORGANIZATION_USERS_UPERCUT, GET_ORGANIZATION_USER_PAGINATION } from '@gql/organization-users';
import { IOrganizationUserFilter } from '../OrganizationUserFilter';

/**
 * Enhanced hook to fetch organization users based on current user's role
 * Automatically selects the appropriate query (Upercut vs Pro)
 * @param query - Search query string
 * @param sortBy - Sort field name (deprecated - use sort.field instead)
 * @param sort - Sort options { field, order }
 * @param pagination - Pagination options
 * @param higherUpId - ID of higher up user to filter subordinates
 * @param deep - Whether to fetch nested subordinates
 */
export const useGetOrganizationUsers = (params: {
    query: string,
    proId?: string,
    sortBy?: string,
    sort?: { field: string; order: string },
    pagination?: { offset?: number; limit?: number },
    higherUpId?: string,
    deep?: boolean,
    filter?: IOrganizationUserFilter,
}) => {
    const { user, isUpercut } = useAuth();
    const { query, proId, sortBy, sort, pagination, higherUpId, deep, filter } = params;
    // Select the appropriate query based on user role
    const queryToUse = isUpercut 
        ? GET_ORGANIZATION_USERS_UPERCUT 
        : GET_ORGANIZATION_USER_PAGINATION;

    const { data, loading, error, refetch } = useQuery(queryToUse, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query,
            sortBy,
            sort,
            pagination,
            higherUpId,
            deep,
            filter,
            // Only pass proId for Upercut queries
            ...(isUpercut && proId ? { proId } : {}),
        },
        skip: !user, // Skip query if user is not loaded
    });

    // Extract data based on which query was used
    const organizationUsersData = isUpercut 
        ? data?.getOrganizationUsersUpercut 
        : data?.getOrganizationUserWithPaginations;

    return {
        organizationUsers: organizationUsersData?.docs ?? [],
        totalDocs: organizationUsersData?.meta?.totalDocs ?? 0,
        totalPages: organizationUsersData?.meta?.totalPages ?? 0,
        meta: organizationUsersData?.meta,
        loading,
        error,
        refetch,
        isUpercut,
        userRole: user?.role,
    };
};
